import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { parseArrayValue } from "../helpers";
import { localizeOptionLabel } from "../system-field-labels";

/**
 * Single-select dropdown for both ENUM and ARRAY fields, built on the
 * shadcn Select primitive (Radix-based). Same humanised labels, same
 * wire-shape difference:
 *
 *   - ENUM  → value is the picked option string (or `null`)
 *   - ARRAY → value is a JSON-stringified single-item array (or `null`).
 *             ARRAY is multi-value at the BE schema level, but the
 *             widget intentionally exposes it as a one-pick affordance
 *             — visitors find a single dropdown far less confusing than
 *             a multi-checklist, and operator field semantics ("Chat
 *             status: IN_PROGRESS") are almost always one-at-a-time in
 *             practice. If a future field genuinely needs multi-select
 *             we'll surface it as a distinct control rather than
 *             overload this one.
 *
 * BE option strings are typically uppercase tokens (`IN_PROGRESS`);
 * `humanise` turns them into sentence case for display while the wire
 * value stays untouched.
 */
export function OptionsControl({
  id,
  fieldKey,
  mode,
  options,
  value,
  disabled,
  onChange,
  t,
}: {
  id: string;
  /** BE field key — drives system-option label lookup (e.g. translating
   *  `chatStatus.IN_PROGRESS` → "In progress" / "В процесі"). Custom
   *  fields fall back to `humanise()` of the wire value. */
  fieldKey: string;
  mode: "single" | "multi";
  options: string[];
  value: string | null;
  disabled?: boolean;
  onChange: (next: string | null) => void;
  t: (key: string) => string;
}) {
  if (options.length === 0) {
    return (
      <p id={id} className="text-[11px] text-wx-fg-subtle italic">
        {t("noOptions")}
      </p>
    );
  }

  // Read the current selection: ENUM is stored raw; ARRAY is a JSON
  // single-item array. parseArrayValue returns [] for nulls/invalid.
  const current = mode === "single" ? (value ?? "") : (parseArrayValue(value)[0] ?? "");

  const handleChange = (next: string) => {
    // Radix Select emits "" only via our `__clear__` sentinel below
    // (it disallows real empty-string values), so a non-empty `next`
    // is always a real pick.
    if (next === "__clear__" || !next) {
      onChange(null);
      return;
    }
    onChange(mode === "single" ? next : JSON.stringify([next]));
  };

  return (
    <Select value={current || undefined} disabled={disabled} onValueChange={handleChange}>
      <SelectTrigger id={id} aria-label={t("selectOption")}>
        <SelectValue placeholder="—" />
      </SelectTrigger>
      <SelectContent>
        {/* Clear-sentinel option lets the visitor unset a non-required
            field — Radix disallows a literal empty-string value, so we
            map a known token through `handleChange` to `null`. */}
        <SelectItem value="__clear__">—</SelectItem>
        {options.map((opt) => (
          <SelectItem key={opt} value={opt}>
            {localizeOptionLabel(fieldKey, opt, t)}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

/**
 * Turn a BE option token (`IN_PROGRESS`, `chat_status`, `nicelyCased`)
 * into sentence-case for display. The wire value is untouched — this
 * is purely a label transform. Acronyms get re-uppercased so `URL` /
 * `ID` / `AI` don't lowercase into `Url` / `Id` / `Ai`.
 */
const ACRONYMS = new Set(["URL", "ID", "AI", "API", "VIP", "CRM", "SMS"]);

export function humanise(token: string): string {
  if (!token) return token;
  // Split on snake/kebab boundaries, then on camelCase boundaries.
  const parts = token
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .split(/[\s_-]+/)
    .filter(Boolean);
  if (parts.length === 0) return token;
  return parts
    .map((part, idx) => {
      const upper = part.toUpperCase();
      if (ACRONYMS.has(upper)) return upper;
      const lower = part.toLowerCase();
      return idx === 0 ? lower.charAt(0).toUpperCase() + lower.slice(1) : lower;
    })
    .join(" ");
}
